import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as R from '../common/constants/routes.constants';

const APP_NAME = 'Veerny Admin';

/**
 * Which section a path belongs to, as a translation key. Matched against the route
 * patterns themselves, not by prefix: kindergarten pages live at `/<id>/...`, and a prefix
 * test would also call any mistyped address "Kindergartens".
 */
const SECTIONS: Array<{ key: string; patterns: string[] }> = [
  { key: 'nav.financeItems.invoices', patterns: [R.FINANCE_INVOICES, R.INVOICE_CREATE, R.INVOICE_DETAILS, R.INVOICE_BATCH_DETAILS] },
  { key: 'nav.financeItems.contracts', patterns: [R.FINANCE_CONTRACTS] },
  { key: 'nav.financeItems.debts', patterns: [R.FINANCE_DEBTS] },
  { key: 'nav.financeItems.settings', patterns: [R.FINANCE_SETTINGS] },
  { key: 'nav.financeItems.platformItems', patterns: [R.FINANCE_PLATFORM_ITEMS] },
  { key: 'nav.finance', patterns: [R.FINANCE, R.FINANCE_LOCATION, R.FINANCE_GROUP] },
  { key: 'nav.infoboard', patterns: [R.INFOBOARD, R.POST_CREATE, R.POST_EDIT] },
  {
    key: 'nav.kindergartens',
    patterns: [
      R.KINDERGARTENS,
      R.KINDERGARTEN_DETAILS,
      R.LOCATIONS_CREATE,
      R.GROUP_DETAILS,
      R.TEACHERS_CREATE,
      R.TEACHER_EDIT,
      R.TEACHER_DETAILS,
      R.CHILD_CREATE,
      R.CHILD_EDIT,
      R.CHILD_DETAILS,
      R.PARENT_CREATE,
      R.PARENT_EDIT,
      R.PARENT_DETAILS,
      R.EVENT_CREATE,
      R.EVENT_DETAILS
    ]
  }
];

/** Routes that are not pages in a section; they keep the bare app name. */
const UNTITLED = [R.CALLBACK, R.UNATHORIZED];

// the constants build kindergarten routes as `${'/'}/:id`, i.e. starting with '//'
const normalise = (pattern: string) => pattern.replace(/\/{2,}/g, '/');

export const titleKeyFor = (pathname: string): string | null => {
  if (UNTITLED.some(pattern => matchPath(normalise(pattern), pathname))) return null;
  const section = SECTIONS.find(({ patterns }) => patterns.some(pattern => matchPath(normalise(pattern), pathname)));
  // anything else is the not-found page
  return section ? section.key : 'errors.notFound';
};

/**
 * "{Section} · Veerny Admin" in the tab, in the interface language. One hook in the layout
 * rather than one per page: the section is all a tab needs to say, and the sidebar already
 * defines what the sections are.
 */
export const useDocumentTitle = (): void => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  useEffect(() => {
    const key = titleKeyFor(pathname);
    document.title = key ? `${t(key)} · ${APP_NAME}` : APP_NAME;
  }, [pathname, t]);
};
